import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router';

import { Drawer, Flex, Layout } from 'antd';

import { toFloat } from '../../utils/convert';
import { getMenuItems } from '../../utils/menu';

import { defaultMenuSettings } from '../../config/menuSettings';
import {
  DEPOSIT_PATH,
  DOCUMENTATION_PATH,
  PROFILE_PATH,
} from '../../constants/routes';
import DisplaySizeContext from '../../context/DisplaySize';
import { useHomeMessageId } from '../../modules/home/hooks/useHomeMessageId';
import AiIcon from '../AiIcon';
import { HeaderEmail } from '../HeaderEmail';
import LangSwitcher from '../LangSwitcher';
import Logout from '../Logout';
import ThemeSwitcher from '../ThemeSwitcher';

import { ReduxState } from '../../types/app';
import { SettingsMenuItem } from '../../types/menu';

const { Header } = Layout;

const emptyConfig = {};

type Props = {
  menu?: SettingsMenuItem[];
};

const CabinetHeader = ({ menu = defaultMenuSettings }: Props) => {
  const intl = useIntl();
  const location = useLocation();
  const displaySize = useContext(DisplaySizeContext);
  const homeMessageId = useHomeMessageId();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isDesktopLg = displaySize === 'desktopLg';
  const balance = useSelector(
    (state: ReduxState) => state.ClientTotalBalance.balance
  );

  const menuItems = getMenuItems(
    emptyConfig,
    displaySize,
    [],
    [],
    location.pathname,
    0,
    menu
  );

  useEffect(() => {
    if (isDesktopLg) {
      setIsDrawerOpen(false);
    }
  }, [isDesktopLg]);

  const renderBalance = (variant: 'desktop' | 'drawer') => (
    <Link
      className={
        variant === 'drawer'
          ? 'header__balance header__drawer-balance'
          : 'header__balance'
      }
      to={DEPOSIT_PATH}
      aria-label={intl.formatMessage({ id: 'files.header.balance' })}
      onClick={variant === 'drawer' ? () => setIsDrawerOpen(false) : undefined}
    >
      <div className="header__balance-icon">
        <AiIcon name="wallet" />
      </div>
      <span className="header__balance-text">
        <span className="header__balance-label">
          <FormattedMessage id="files.header.balance" />
        </span>
        <span className="header__balance-value">
          {toFloat(balance, 4).toFixed(2)}$
        </span>
      </span>
    </Link>
  );

  const renderMenuItems = (variant: 'desktop' | 'drawer') =>
    menuItems.map(({ name, url, label, icon, isActive }) => {
      const href = url || '#';
      const isRouteLink = href.startsWith('/');
      const isDrawer = variant === 'drawer';

      const linkClassName = [
        isDrawer ? 'header__drawer-nav-link' : 'header__nav-link',
        'menu__link',
        isActive
          ? `${isDrawer ? 'header__drawer-nav-link' : 'header__nav-link'}_active menu__link_active`
          : '',
      ]
        .filter(Boolean)
        .join(' ');

      const content = (
        <>
          {icon && (
            <span
              className={
                isDrawer
                  ? 'header__nav-icon header__drawer-nav-icon'
                  : 'header__nav-icon'
              }
            >
              <AiIcon name={icon} />
            </span>
          )}
          {label && <FormattedMessage id={label} />}
        </>
      );

      if (isRouteLink) {
        return (
          <Link
            key={name}
            className={linkClassName}
            to={href}
            aria-current={isActive ? 'page' : undefined}
            onClick={isDrawer ? () => setIsDrawerOpen(false) : undefined}
          >
            {content}
          </Link>
        );
      }

      return (
        <a
          key={name}
          className={linkClassName}
          href={href}
          onClick={(event) => event.preventDefault()}
        >
          {content}
        </a>
      );
    });

  return (
    <Header className="header">
      <Link className="header__brand" to="/">
        <AiIcon name="header-logo-img" />
        <span className="header__brand-logo-text">
          <span className="header__brand-logo-text-primary">
            <FormattedMessage id={homeMessageId('brand.primary')} />
          </span>{' '}
          <span className="header__brand-logo-text-secondary">
            <FormattedMessage id={homeMessageId('brand.secondary')} />
          </span>
        </span>
      </Link>

      {isDesktopLg ? (
        <>
          <nav
            className={`header__nav menu-level0 menu-level0-${displaySize}`}
            aria-label={intl.formatMessage({ id: 'files.nav.ariaLabel' })}
          >
            {renderMenuItems('desktop')}
          </nav>

          <Flex className="header__actions" align="center">
            {renderBalance('desktop')}
            <Link
              className="header__actions-item"
              to={`${DOCUMENTATION_PATH}#/category1/Article%201`}
              aria-label={intl.formatMessage({ id: 'files.header.helpText' })}
            >
              <AiIcon name="question" />
            </Link>

            <ThemeSwitcher />

            <div className="header__language">
              <LangSwitcher />
            </div>

            <HeaderEmail to={PROFILE_PATH} />

            <Logout />
          </Flex>
        </>
      ) : (
        <>
          <button
            type="button"
            className="header__burger"
            aria-label={intl.formatMessage({ id: 'files.nav.ariaLabel' })}
            aria-expanded={isDrawerOpen}
            onClick={() => setIsDrawerOpen(true)}
          >
            <AiIcon name="burgerMenu" />
          </button>

          <Drawer
            className="header__drawer"
            placement="left"
            width={352}
            open={isDrawerOpen}
            closable={false}
            onClose={() => setIsDrawerOpen(false)}
            title={
              <div className="header__drawer-header header__drawer-header--stacked">
                <HeaderEmail
                  className="header__drawer-email"
                  to={PROFILE_PATH}
                  onClick={() => setIsDrawerOpen(false)}
                />

                <Logout />

                <div className="header__language header__drawer-language">
                  <LangSwitcher />
                </div>

                <ThemeSwitcher />

                {renderBalance('drawer')}
              </div>
            }
          >
            <nav
              className={`header__drawer-nav menu-level0 menu-level0-${displaySize}`}
              aria-label={intl.formatMessage({ id: 'files.nav.ariaLabel' })}
            >
              {renderMenuItems('drawer')}
            </nav>
          </Drawer>
        </>
      )}
    </Header>
  );
};

export default CabinetHeader;
